import React from 'react';
import { Container, Icon, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useAuthContext from '../../hooks/useAuthContext';
import {
  UserManagementPage,
  ChipNumbersPage,
  FarmsPage,
  LanguageSettings,
  VetsPage,
  SlaughterhousesPage
} from '../../components';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import UserRole from '../../constants/user-role';
import useFeatures, { FeatureType } from '../../hooks/features';

const SystemSettings: React.FC<any> = () => {
  const { option } = useParams<{ option: string }>();
  const styles = sxStyles(useTheme());
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasFeature } = useFeatures();

  const handleTabChange = (_event: React.ChangeEvent<object>, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate('/settings/farms');
        break;

      case 1:
        navigate('/settings/vets');
        break;

      case 2:
        navigate('/settings/language');
        break;

      case 3:
        navigate('/settings/chip-numbers');
        break;

      case 4:
        navigate('/settings/user-management');
        break;

      case 5:
        navigate('/settings/slaughterhouses');
        break;
    }
  };

  const getTabIndex = (): number => {
    if (option && option === 'farms') {
      return 0;
    }

    if (option && option === 'vets') {
      return 1;
    }

    if (option && option === 'language') {
      return 2;
    }

    if (option && option === 'chip-numbers') {
      return 3;
    }

    if (option && option === 'user-management') {
      return 4;
    }

    if (option && option === 'slaughterhouses') {
      return 5;
    }

    return 0;
  };

  React.useEffect(() => {
    const tabIndex = getTabIndex();
    setTab(tabIndex);
  }, [option]);

  const [tab, setTab] = React.useState(getTabIndex());

  return state.role === UserRole.manager || state.role === UserRole.admin ? (
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item container md={4} xs={12} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'admin_panel_settings'}
                </Icon>
              </Grid>
              <Grid item>
                <Typography component="h2" variant="h6" color="primary">
                  {t('f-settings-sys')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="column" sx={styles.mt1}>
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab label={t('f-farms')} id="farmsTab" />
              <Tab label={t('f-vets')} id="vetsTab" />
              <Tab label={t('f-settings-lang')} id="langTab" />
              <Tab label={t('f-chip-numbers')} id="chipNumbersTab" />
              <Tab label={t('f-user-management')} id="userManagementTab" />
              {hasFeature(FeatureType.USE_SLAUGHTERHOUSE_MANAGEMENT) && (
                <Tab label={t('f-slaughterhouses')} id="slaughterhousesTab" />
              )}
            </Tabs>
            <Divider />
          </Grid>

          <Paper sx={styles.paper} elevation={1}>
            <Grid container>
              {tab === 0 && (
                <Grid item xs={12} md={12}>
                  <FarmsPage />
                </Grid>
              )}
              {tab === 1 && (
                <Grid item xs={12} md={12}>
                  <VetsPage />
                </Grid>
              )}
              {tab === 2 && (
                <Grid item xs={12} md={12}>
                  <LanguageSettings />
                </Grid>
              )}
              {tab === 3 && (
                <Grid item xs={12} md={12}>
                  <ChipNumbersPage />
                </Grid>
              )}
              {tab === 4 && (
                <Grid item xs={12} md={12}>
                  <UserManagementPage />
                </Grid>
              )}
              {tab === 5 && hasFeature(FeatureType.USE_SLAUGHTERHOUSE_MANAGEMENT) && (
                <Grid item xs={12} md={12}>
                  <SlaughterhousesPage />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  ) : (
    <Navigate to={{ pathname: '/dashboard' }} />
  );
};

export default SystemSettings;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      minHeight: 200
    },
    spinnerBox: {
      display: 'flex',
      justifyContent: 'center'
    },
    formControl: {
      margin: theme.spacing(1)
    },
    mt1: {
      marginTop: theme.spacing(1)
    }
  }) as const;
